/**
 * Half-hour drop target used in week + day views. The caller lays out
 * a stack of these on top of (or instead of) the hour grid so that a
 * task drag resolves to a precise start time.
 *
 * Identity: `time-slot:<date-iso>:<startMinute>` — `startMinute` is the
 * minute-of-day (0..1410 at 30-min boundaries), so the same cell across
 * days has a distinct id. The drag payload gets `{ dateIso, startMinute }`.
 */

import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { format } from 'date-fns'

export const TIME_SLOT_DROP_TYPE = 'time-slot'

export interface TimeSlotDropData {
  kind: typeof TIME_SLOT_DROP_TYPE
  dateIso: string
  startMinute: number
}

interface TimeSlotDropZoneProps {
  date: Date
  startMinute: number
  height: number
  /**
   * When true (the default), the zone is invisible until a drag is over
   * it. Set false to debug layout. The parent still controls whether
   * a zone is rendered at all, e.g. suppress during non-drag state.
   */
  subtle?: boolean
}

export const TimeSlotDropZone: React.FC<TimeSlotDropZoneProps> = ({
  date,
  startMinute,
  height,
  subtle = true,
}) => {
  const dateIso = format(date, 'yyyy-MM-dd')
  const { setNodeRef, isOver } = useDroppable({
    id: `${TIME_SLOT_DROP_TYPE}:${dateIso}:${startMinute}`,
    data: { kind: TIME_SLOT_DROP_TYPE, dateIso, startMinute } satisfies TimeSlotDropData,
  })
  return (
    <div
      ref={setNodeRef}
      style={{ height: `${height}px` }}
      className={`w-full transition-colors ${
        isOver
          ? 'bg-brand-primary/20 ring-1 ring-brand-primary'
          : subtle
            ? ''
            : 'ring-1 ring-dashed ring-brand-primary/20'
      }`}
      aria-hidden={!isOver}
    />
  )
}

export function formatSlotLabel(startMinute: number): string {
  const hh = Math.floor(startMinute / 60)
  const mm = startMinute % 60
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  return format(d, 'h:mm a')
}

export function minutesToTimeString(startMinute: number): string {
  const hh = Math.floor(startMinute / 60)
  const mm = startMinute % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

export default TimeSlotDropZone
